import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Fuel, Calculator, BadgeCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_FEES = { Lagos: 50000, Ogun: 60000, Oyo: 90000, Osun: 110000, Ondo: 120000, Ekiti: 120000, Kwara: 130000, "FCT Abuja": 200000, Edo: 160000, Delta: 180000, Rivers: 200000, Kano: 300000 };
const FALLBACK_PRICE = 1100;

const fmt = (n) => "\u20A6" + n.toLocaleString("en-NG");

export default function DieselOrder() {
  const [pricing, setPricing] = useState({ price: FALLBACK_PRICE, fees: FALLBACK_FEES });
  const [form, setForm] = useState({ name: "", phone: "", address: "", state: "", litres: "", delivery_date: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pricing`).then(({ data }) => {
      setPricing({ price: data.price_per_litre, fees: data.delivery_fees });
    }).catch(() => {});
  }, []);

  const litres = parseInt(form.litres, 10) || 0;
  const deliveryFee = form.state ? pricing.fees[form.state] ?? 250000 : 0;
  const estimate = useMemo(() => (litres > 0 && form.state ? litres * pricing.price + deliveryFee : 0), [litres, form.state, pricing, deliveryFee]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.state || litres <= 0) {
      toast.error("Please fill in your name, phone, address, state and litres.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/orders`, { ...form, litres });
      toast.success(`Order ${data.order_ref} received!`, {
        description: `Estimated total: ${fmt(data.estimated_total)}. Our team will call ${form.phone} to confirm.`,
      });
      setForm({ name: "", phone: "", address: "", state: "", litres: "", delivery_date: "", notes: "" });
    } catch {
      toast.error("Could not submit your order. Please call us on +234 706 379 2622.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="order" className="py-20 md:py-28 bg-[#0B132B] relative overflow-hidden grain-overlay" data-testid="order-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-5 gap-14">
        <div className="lg:col-span-2">
          <p className="text-amber-500 font-display font-semibold tracking-[0.35em] uppercase text-xs mb-4" data-testid="order-eyebrow">Door-to-Door Diesel</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl text-white leading-tight" data-testid="order-heading">
            Diesel Delivered to Your <span className="text-gradient-amber">Doorstep</span>
          </h2>
          <p className="mt-5 text-slate-400 leading-relaxed" data-testid="order-description">
            Homes, estates, offices, factories, hospitals, banks, telecom sites — wherever you need premium AGO, we bring it to you. Place your order below and our team will confirm immediately.
          </p>
          <ul className="mt-8 space-y-4" data-testid="order-benefits">
            {["Premium-grade, metered diesel — exactly what you pay for", "Same-day and scheduled deliveries available", "Serving Lagos, Ogun, Oyo and states nationwide"].map((b) => (
              <li key={b} className="flex items-start gap-3 text-slate-300 text-sm">
                <BadgeCheck size={18} className="text-amber-500 mt-0.5 shrink-0" /> {b}
              </li>
            ))}
          </ul>
          <div className="mt-10 bg-[#1C2541]/70 border border-slate-700/50 rounded-2xl p-6" data-testid="order-estimate-card">
            <div className="flex items-center gap-2 text-slate-300 text-sm font-semibold mb-3">
              <Calculator size={16} className="text-amber-500" /> Live Estimate
            </div>
            <div className="space-y-1.5 text-sm text-slate-400 font-mono-stat">
              <p data-testid="estimate-rate">Rate: {fmt(pricing.price)} / litre</p>
              <p data-testid="estimate-volume">Volume: {litres.toLocaleString()} L &rarr; {fmt(litres * pricing.price)}</p>
              <p data-testid="estimate-delivery-fee">Delivery ({form.state || "select state"}): {form.state ? fmt(deliveryFee) : "\u2014"}</p>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-700/50">
              <p className="text-xs text-slate-500 uppercase tracking-widest">Estimated Total</p>
              <p className="font-mono-stat text-3xl font-bold text-amber-500 mt-1" data-testid="estimate-total">{estimate ? fmt(estimate) : "\u20A60"}</p>
              <p className="text-xs text-slate-500 mt-2">Final price confirmed on call, based on the prevailing depot rate.</p>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 bg-[#1C2541]/70 border border-slate-700/50 rounded-3xl p-7 md:p-10 backdrop-blur-md" data-testid="diesel-order-form">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-amber-600/15 flex items-center justify-center"><Fuel size={22} className="text-amber-500" /></div>
            <h3 className="font-display font-bold text-xl md:text-2xl text-white">Place Your Delivery Order</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="order-name" className="text-slate-300">Full Name *</Label>
              <Input id="order-name" data-testid="order-name-input" value={form.name} onChange={set("name")} placeholder="e.g. Adewale Johnson" className="bg-[#0B132B] border-slate-700 text-slate-100 placeholder:text-slate-600 focus-visible:ring-amber-600" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="order-phone" className="text-slate-300">Phone Number *</Label>
              <Input id="order-phone" data-testid="order-phone-input" value={form.phone} onChange={set("phone")} placeholder="e.g. 0803 000 0000" className="bg-[#0B132B] border-slate-700 text-slate-100 placeholder:text-slate-600 focus-visible:ring-amber-600" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="order-address" className="text-slate-300">Delivery Address *</Label>
              <Input id="order-address" data-testid="order-address-input" value={form.address} onChange={set("address")} placeholder="Street, area, city" className="bg-[#0B132B] border-slate-700 text-slate-100 placeholder:text-slate-600 focus-visible:ring-amber-600" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Delivery State *</Label>
              <Select value={form.state} onValueChange={set("state")}>
                <SelectTrigger data-testid="order-state-select" className="bg-[#0B132B] border-slate-700 text-slate-100 focus:ring-amber-600">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C2541] border-slate-700 text-slate-100">
                  {Object.keys(pricing.fees).map((s) => (
                    <SelectItem key={s} value={s} data-testid={`order-state-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>
                  ))}
                  <SelectItem value="Other" data-testid="order-state-option-other">Other (Nationwide)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="order-litres" className="text-slate-300">Quantity (Litres) *</Label>
              <Input id="order-litres" data-testid="order-litres-input" type="number" min="1" value={form.litres} onChange={set("litres")} placeholder="e.g. 1000" className="bg-[#0B132B] border-slate-700 text-slate-100 placeholder:text-slate-600 focus-visible:ring-amber-600" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="order-date" className="text-slate-300">Preferred Delivery Date</Label>
              <Input id="order-date" data-testid="order-date-input" type="date" value={form.delivery_date} onChange={set("delivery_date")} className="bg-[#0B132B] border-slate-700 text-slate-100 focus-visible:ring-amber-600" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="order-notes" className="text-slate-300">Notes (optional)</Label>
              <Textarea id="order-notes" data-testid="order-notes-input" value={form.notes} onChange={set("notes")} placeholder="Access instructions, generator capacity, etc." className="bg-[#0B132B] border-slate-700 text-slate-100 placeholder:text-slate-600 focus-visible:ring-amber-600 min-h-[42px]" />
            </div>
          </div>
          <Button type="submit" disabled={submitting} data-testid="order-submit-btn" className="mt-8 w-full bg-amber-600 hover:bg-amber-500 text-white rounded-full h-12 text-base font-semibold transition-all duration-200 disabled:opacity-60">
            {submitting ? "Submitting Order..." : "Submit Diesel Order"}
          </Button>
        </form>
      </div>
    </section>
  );
}
